import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Gem, Lock, Unlock, Clock, TrendingUp, Gift, AlertTriangle, RefreshCw, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_COLORS = {
  flex: { bg: '#00CED1', border: '#00CED1', label: 'Flex' },
  standard: { bg: '#D4A017', border: '#D4A017', label: 'Standard' },
  power_lock: { bg: '#E8520F', border: '#E8520F', label: 'Power Lock' },
};

export default function Staking() {
  const { panicMode } = useOutletContext();
  const [plans, setPlans] = useState({});
  const [activeStakes, setActiveStakes] = useState({ stakes: [], total_staked: 0, total_pending_bonus: 0, active_count: 0 });
  const [history, setHistory] = useState({ stakes: [], total_earned_credits: 0, total_bonus_forfeited: 0 });
  const [selectedPlan, setSelectedPlan] = useState('flex');
  const [stakeAmount, setStakeAmount] = useState('');
  const [glabsBalance, setGlabsBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, activeRes, balRes] = await Promise.all([
        axios.get(`${API_URL}/api/staking/plans`),
        axios.get(`${API_URL}/api/staking/active`),
        axios.get(`${API_URL}/api/glabs/balance`),
      ]);
      setPlans(plansRes.data.plans);
      setActiveStakes(activeRes.data);
      setGlabsBalance(balRes.data.glabs_balance);
    } catch (err) { console.error(err); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/staking/history`);
      setHistory(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleStake = async () => {
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/staking/create`, {
        plan: selectedPlan, glabs_amount: parseFloat(stakeAmount),
      });
      toast.success(data.message);
      setStakeAmount('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Staking failed'); }
    setLoading(false);
  };

  const handleClaim = async (stakeId) => {
    setClaimingId(stakeId);
    try {
      const { data } = await axios.post(`${API_URL}/api/staking/claim/${stakeId}`);
      toast.success(data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Claim failed'); }
    setClaimingId(null);
  };

  const handleEarlyWithdraw = async (stakeId) => {
    setClaimingId(stakeId);
    try {
      const { data } = await axios.post(`${API_URL}/api/staking/withdraw/${stakeId}`);
      toast.success(data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Withdrawal failed'); }
    setClaimingId(null);
  };

  const plan = plans[selectedPlan];
  const previewBonus = stakeAmount && plan ? (parseFloat(stakeAmount) * plan.bonus_pct / 100) : 0;
  const previewTotal = stakeAmount && plan ? (parseFloat(stakeAmount) + previewBonus) : 0;

  return (
    <div className="space-y-6" data-testid="staking-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <Gem className="w-7 h-7 text-[#D4A017]" /> GLabs Staking Vault
          </h1>
          <p className="text-[hsl(215,16%,56%)]">Stake GLabs tokens, earn bonus Credits at maturity</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="credit-bar">
            <Gem className="w-4 h-4 text-[#D4A017]" />
            <span className="font-mono font-semibold text-[#D4A017]">{glabsBalance.toFixed(0)}</span>
            <span className="text-[10px] text-[#D4A017]">GLabs Available</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }}
            className="border-[hsl(220,16%,22%)] text-white bg-transparent hover:bg-[hsl(220,16%,16%)]" data-testid="toggle-history">
            <Clock className="w-3.5 h-3.5 mr-1" /> {showHistory ? 'Hide' : 'Show'} History
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="solar-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#D4A017]/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-[#D4A017]" />
          </div>
          <div>
            <p className="text-sm text-[hsl(215,16%,56%)]">Total Staked</p>
            <p className="text-2xl font-bold text-white">{activeStakes.total_staked.toFixed(0)} <span className="text-sm text-[#D4A017]">GLabs</span></p>
          </div>
        </div>
        <div className="solar-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
            <Gift className="w-6 h-6 text-[#10B981]" />
          </div>
          <div>
            <p className="text-sm text-[hsl(215,16%,56%)]">Pending Bonus</p>
            <p className="text-2xl font-bold text-[#10B981]">+{activeStakes.total_pending_bonus.toFixed(0)} <span className="text-sm">Credits</span></p>
          </div>
        </div>
        <div className="solar-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#E8520F]/10 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-[#E8520F]" />
          </div>
          <div>
            <p className="text-sm text-[hsl(215,16%,56%)]">Active Stakes</p>
            <p className="text-2xl font-bold text-white">{activeStakes.active_count}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Create Stake */}
        <Card className="border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)] shadow-none">
          <CardHeader>
            <CardTitle className="text-white font-['Outfit']">Create New Stake</CardTitle>
            <CardDescription className="text-[hsl(215,16%,56%)]">Choose a plan and lock your GLabs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Plan Selector */}
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(plans).map(([key, p]) => {
                const colors = PLAN_COLORS[key] || PLAN_COLORS.flex;
                const isSelected = selectedPlan === key;
                return (
                  <button key={key} onClick={() => setSelectedPlan(key)}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      isSelected ? `border-[${colors.border}] bg-[${colors.bg}]/5` : 'border-[hsl(220,16%,18%)] hover:border-[hsl(220,16%,24%)]'
                    }`}
                    style={isSelected ? { borderColor: colors.border, background: `${colors.bg}08` } : {}}
                    data-testid={`plan-${key}`}>
                    <p className="text-sm font-bold text-white">{p.name}</p>
                    <p className="text-2xl font-bold mt-1" style={{ color: colors.bg }}>+{p.bonus_pct}%</p>
                    <p className="text-xs text-[hsl(215,16%,45%)] mt-1">{p.duration_days} days</p>
                    <p className="text-[10px] text-[hsl(215,16%,40%)]">Min: {p.min_stake} GLabs</p>
                  </button>
                );
              })}
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
              <Label className="text-white text-sm">GLabs Amount to Stake</Label>
              <Input type="number" value={stakeAmount} onChange={e => setStakeAmount(e.target.value)}
                placeholder={`Min ${plan?.min_stake || 10} GLabs`}
                max={glabsBalance}
                className="border-[hsl(220,16%,22%)] bg-[hsl(220,16%,14%)] text-white"
                data-testid="stake-amount-input" />
              <div className="flex items-center justify-between text-xs text-[hsl(215,16%,45%)]">
                <span>Available: {glabsBalance.toFixed(0)} GLabs</span>
                <button onClick={() => setStakeAmount(String(Math.floor(glabsBalance)))}
                  className="text-[#D4A017] hover:underline" data-testid="stake-max-btn">MAX</button>
              </div>
            </div>

            {/* Preview */}
            {stakeAmount && parseFloat(stakeAmount) > 0 && plan && (
              <div className="p-3 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,22%)]" data-testid="stake-preview">
                <p className="text-xs text-[hsl(215,16%,56%)] mb-2">Maturity Preview</p>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-[#D4A017] font-bold">{parseFloat(stakeAmount).toFixed(0)} GLabs</span>
                  <ArrowRight className="w-4 h-4 text-[hsl(215,16%,40%)]" />
                  <span className="font-mono text-[#10B981] font-bold">{previewTotal.toFixed(0)} Credits</span>
                  <span className="text-xs text-[#10B981]">(+{previewBonus.toFixed(0)} bonus)</span>
                </div>
                <p className="text-[10px] text-[hsl(215,16%,40%)] mt-1">Matures in {plan.duration_days} days | Early exit forfeits bonus</p>
              </div>
            )}

            <Button onClick={handleStake}
              disabled={loading || !stakeAmount || parseFloat(stakeAmount) < (plan?.min_stake || 10) || parseFloat(stakeAmount) > glabsBalance}
              className="w-full bg-[#D4A017] hover:bg-[#B8880F] text-white" data-testid="stake-submit-btn">
              <Lock className="w-4 h-4 mr-2" />
              {loading ? 'Staking...' : `Stake ${stakeAmount || '0'} GLabs in ${plan?.name || ''} Plan`}
            </Button>
          </CardContent>
        </Card>

        {/* Active Stakes */}
        <Card className="border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)] shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white font-['Outfit']">Active Stakes</CardTitle>
                <CardDescription className="text-[hsl(215,16%,56%)]">{activeStakes.active_count} active locks</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}
                className="border-[hsl(220,16%,22%)] text-white bg-transparent hover:bg-[hsl(220,16%,16%)]">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {activeStakes.stakes.length > 0 ? (
              <div className="space-y-3">
                {activeStakes.stakes.map(stake => {
                  const colors = PLAN_COLORS[stake.plan] || PLAN_COLORS.flex;
                  return (
                    <div key={stake.stake_id}
                      className={`p-4 rounded-xl border transition-all ${
                        stake.is_matured ? 'border-[#10B981]/50 bg-[#10B981]/[0.03]' : 'border-[hsl(220,16%,18%)] bg-[hsl(220,16%,14%)]'
                      }`} data-testid={`stake-${stake.stake_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ background: `${colors.bg}15`, color: colors.bg }}>
                            {stake.plan_name}
                          </span>
                          <span className="font-mono text-sm font-bold text-white">{stake.glabs_staked.toFixed(0)} GLabs</span>
                          {stake.is_matured && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#10B981]/15 text-[#10B981] animate-pulse">MATURED</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {stake.is_matured ? (
                            <Button size="sm" onClick={() => handleClaim(stake.stake_id)}
                              disabled={claimingId === stake.stake_id}
                              className="bg-[#10B981] hover:bg-[#059669] text-white h-8" data-testid={`claim-${stake.stake_id}`}>
                              <Gift className="w-3.5 h-3.5 mr-1" />
                              {claimingId === stake.stake_id ? 'Claiming...' : `Claim ${stake.total_credits_at_maturity.toFixed(0)}`}
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => handleEarlyWithdraw(stake.stake_id)}
                              disabled={claimingId === stake.stake_id}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10 bg-transparent h-8" data-testid={`withdraw-${stake.stake_id}`}>
                              <Unlock className="w-3.5 h-3.5 mr-1" />
                              {claimingId === stake.stake_id ? 'Withdrawing...' : 'Early Exit'}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-2">
                        <div className="flex items-center justify-between text-[10px] text-[hsl(215,16%,45%)] mb-1">
                          <span>{stake.time_remaining} remaining</span>
                          <span>{stake.progress_pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[hsl(220,16%,18%)] overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${stake.progress_pct}%`,
                              background: stake.is_matured ? '#10B981' : colors.bg,
                            }} />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[hsl(215,16%,45%)]">
                          +{stake.bonus_pct}% bonus ({stake.bonus_credits.toFixed(0)} Credits)
                        </span>
                        <span className="font-mono text-[#10B981] font-medium">
                          → {stake.total_credits_at_maturity.toFixed(0)} Credits
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 text-[hsl(215,16%,56%)]">
                <Lock className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No active stakes</p>
                <p className="text-xs mt-1">Stake GLabs to earn bonus Credits</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      {showHistory && (
        <Card className="border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)] shadow-none" data-testid="staking-history">
          <CardHeader>
            <CardTitle className="text-white font-['Outfit'] text-base">Staking History</CardTitle>
            <CardDescription className="text-[hsl(215,16%,56%)]">
              Total earned: {history.total_earned_credits.toFixed(0)} Credits | Forfeited: {history.total_bonus_forfeited.toFixed(0)} Credits
            </CardDescription>
          </CardHeader>
          <CardContent>
            {history.stakes.length > 0 ? (
              <div className="space-y-2">
                {history.stakes.map(s => (
                  <div key={s.stake_id} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
                    <div className="flex items-center gap-3">
                      <span className="text-xs px-2 py-0.5 rounded font-medium" style={{
                        background: `${(PLAN_COLORS[s.plan] || PLAN_COLORS.flex).bg}15`,
                        color: (PLAN_COLORS[s.plan] || PLAN_COLORS.flex).bg
                      }}>{s.plan_name}</span>
                      <span className="text-sm font-mono text-white">{s.glabs_staked.toFixed(0)} GLabs</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {s.status === 'claimed' && (
                        <span className="text-xs text-[#10B981] font-mono">+{s.total_credits_at_maturity.toFixed(0)} Credits</span>
                      )}
                      {s.status === 'withdrawn_early' && (
                        <span className="text-xs text-red-400 font-mono">Forfeited +{s.bonus_credits.toFixed(0)}</span>
                      )}
                      <span className={`text-[10px] px-2 py-0.5 rounded ${
                        s.status === 'claimed' ? 'bg-[#10B981]/15 text-[#10B981]' :
                        s.status === 'withdrawn_early' ? 'bg-red-500/15 text-red-400' :
                        'bg-[#D4A017]/15 text-[#D4A017]'
                      }`}>{s.status === 'claimed' ? 'Claimed' : s.status === 'withdrawn_early' ? 'Early Exit' : 'Active'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[hsl(215,16%,50%)] text-center py-4">No staking history yet</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
